import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload, FileText, BookOpen, Clock, FileCheck, X } from "lucide-react";
import axios from "axios";
import toast from 'react-hot-toast';
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import LedProgressLine from "@/components/LedProgressLine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

// Remove old token function - now using global Firebase auth via axios interceptor

const LegalSummarisation = ({ onBack }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [summaryResults, setSummaryResults] = useState(null);

  // Set page title
  useEffect(() => {
    document.title = "Legal Summarisation - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File size must be under 10MB. Please select a smaller file.");
        return;
      }
      setFile(selectedFile);
      setSummaryResults(null);
    }
  };

  const handleSummarize = async () => {
    if (!file) {
      toast.error("Please select a document to summarize.");
      return;
    }

    setSummarizing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/legal-summary/summarize`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSummaryResults(response.data);
      toast.success("Document summarized successfully!");
    } catch (error) {
      console.error('Summarization error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to summarize document. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSummarizing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setSummaryResults(null);
    if (document.getElementById('file-input')) {
      document.getElementById('file-input').value = '';
    }
  };


  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <BookOpen size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Legal Summarisation" />
            <p className="tool-subtitle">Quickly summarize long legal documents like judgments and reports</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!summaryResults ? (
          <>
            {/* Upload and Summarize Card */}
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Document Summarisation</CardTitle>
                <CardDescription>
                  Upload a legal document and get a concise, comprehensive summary with key points
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="upload-label">Legal Document *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file-input"
                      accept=".pdf,.docx,.txt"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-input" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {file ? <FileText size={48} /> : <Upload size={48} />}
                      </div>
                      <p className="upload-text-tool">
                        {file ? file.name : 'Click to upload document (PDF, DOCX, or TXT)'}
                      </p>
                      <p className="upload-subtext-tool">Maximum file size: 10MB</p>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                onClick={handleSummarize}
                disabled={summarizing || !file}
                loading={summarizing}
                size="lg"
              >
                Summarize
              </AnimatedActionButton>
                  <LedProgressLine isActive={summarizing} />
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          /* Results View */
          <>
            <div className="results-header">
              <h2>Summary Results</h2>
              <Button onClick={handleReset} variant="outline">
                New Summary
              </Button>
            </div>

            {/* Document Info Card */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Document Information</CardTitle>
                <CardDescription>Summary statistics and document type</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="doc-info-grid">
                  <div className="doc-info-card">
                    <div className="doc-info-icon">
                      <Clock size={28} />
                    </div>
                    <div className="doc-info-content">
                      <div className="doc-info-label">Time Saved</div>
                      <div className="doc-info-value">
                        ~{Math.round((summaryResults.word_count_original - summaryResults.word_count_summary) / 20)} min
                      </div>
                    </div>
                  </div>

                  <div className="doc-info-card">
                    <div className="doc-info-icon">
                      <FileText size={28} />
                    </div>
                    <div className="doc-info-content">
                      <div className="doc-info-label">Original</div>
                      <div className="doc-info-value">{summaryResults.word_count_original.toLocaleString()}</div>
                      <div className="doc-info-sublabel">words</div>
                    </div>
                  </div>

                  <div className="doc-info-card">
                    <div className="doc-info-icon">
                      <BookOpen size={28} />
                    </div>
                    <div className="doc-info-content">
                      <div className="doc-info-label">Summary</div>
                      <div className="doc-info-value">{summaryResults.word_count_summary.toLocaleString()}</div>
                      <div className="doc-info-sublabel">words</div>
                    </div>
                  </div>

                  <div className="doc-info-card">
                    <div className="doc-info-icon">
                      <FileCheck size={28} />
                    </div>
                    <div className="doc-info-content">
                      <div className="doc-info-label">Type</div>
                      <div className="doc-info-value-small">{summaryResults.document_type}</div>
                    </div>
                  </div>
        <ReportIssueBar toolName="Legal Summarisation" />
                </div>
              </CardContent>
            </Card>

            {/* Key Points */}
            {summaryResults.key_points && summaryResults.key_points.length > 0 && (
              <Card className="result-card">
                <CardHeader>
                  <CardTitle>Key Points</CardTitle>
                  <CardDescription>The most important takeaways from this document</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="differences-list">
                    {summaryResults.key_points.map((point, index) => (
                      <li key={index} className="difference-item">
                        {point}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Full Summary */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>Comprehensive Summary</CardTitle>
                <CardDescription>Detailed summary of the document</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="analysis-content"
                  dangerouslySetInnerHTML={{ __html: summaryResults.summary }}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default LegalSummarisation;

